// src/pages/ArtifactInfo.jsx

import React, { useEffect, useState, useRef } from "react";
import api from "../api";
import { useParams } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Alert,
  Button,
  Divider
} from "@mui/material";

const SERVER = window.location.origin;

export default function ArtifactInfo() {
  const { id } = useParams();
  const [artifact, setArtifact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState("");

  const [autoplayFailed, setAutoplayFailed] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);

  const audioRef = useRef();

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        setError("");

        const res = await api.get(`/api/artifacts/${id}`);
        setArtifact(res.data);
      } catch (err) {
        console.error(err);
        setError(
          err?.response?.status === 404
            ? "Artifact not found or QR is invalid."
            : "Failed to load artifact. Please check Museum Wi-Fi."
        );
      } finally {
        setLoading(false);
      }
    };

    const visit = async () => {
      try {
        await api.post(`/api/artifacts/visit/${id}`);
      } catch {}
    };

    load();
    visit();
  }, [id]);

  // Try autoplay
  useEffect(() => {
    if (artifact && audioRef.current) {
      const tryPlay = async () => {
        try {
          await audioRef.current.play();
          setAutoplayFailed(false);
        } catch {
          setAutoplayFailed(true);
        }
      };
      tryPlay();
    }
  }, [artifact]);

  if (loading)
    return (
      <Typography sx={{ textAlign: "center", mt: 4 }}>
        Loading artifact…
      </Typography>
    );

  return (
    <Box sx={{ p: 2 }}>
      <Paper sx={{ p: 3, maxWidth: 800, margin: "auto" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!error && artifact && (
          <>
            {/* TITLE */}
            <Typography variant="h4" gutterBottom>
              {artifact.title}
            </Typography>

            {/* IMAGE */}
            {artifact.imageUrl && (
              <img
                src={`${SERVER}/${artifact.imageUrl.replace(/^\//, "")}`}
                alt={artifact.title}
                style={{
                  width: "100%",
                  maxHeight: 380,
                  objectFit: "contain",
                  marginBottom: 12
                }}
              />
            )}

            {/* DESCRIPTION */}
            <Typography variant="body1" sx={{ mb: 2 }}>
              {artifact.description}
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* AUDIO PLAYER */}
            {artifact.audioUrl && (
              <>
                <Typography variant="h6" gutterBottom>
                  🎧 Audio Guide
                </Typography>

                <audio
                  ref={audioRef}
                  controls
                  style={{ width: "100%" }}
                  onError={() =>
                    alert("Audio file missing. Please contact admin.")
                  }
                >
                  <source
                    src={`${SERVER}/${artifact.audioUrl.replace(/^\//, "")}`}
                    type="audio/mpeg"
                  />
                </audio>

                {/* Fallback play button */}
                {autoplayFailed && (
                  <Box sx={{ textAlign: "center", mt: 2 }}>
                    <Typography variant="subtitle1">
                      Tap below to start audio
                    </Typography>

                    <Button
                      variant="contained"
                      onClick={() => {
                        audioRef.current?.play();
                        setAutoplayFailed(false);
                      }}
                      sx={{ mt: 1 }}
                    >
                      ▶ Play Audio
                    </Button>
                  </Box>
                )}
              </>
            )}

            <Divider sx={{ my: 2 }} />

            {/* TRANSCRIPT SECTION */}
            <Typography variant="h6" gutterBottom>
              📜 Transcript
            </Typography>

            <Button
              variant="outlined"
              onClick={() => setShowTranscript(!showTranscript)}
              sx={{ mb: 1 }}
            >
              {showTranscript ? "Hide Transcript" : "Show Transcript"}
            </Button>

            {showTranscript && (
              <Paper
                sx={{
                  p: 2,
                  maxHeight: 260,
                  overflowY: "auto",
                  border: "1px solid #ddd"
                }}
              >
                <Typography
                  variant="body1"
                  sx={{ whiteSpace: "pre-line" }}
                >
                  {artifact.transcript?.trim()
                    ? artifact.transcript
                    : "Transcript not available."}
                </Typography>
              </Paper>
            )}
          </>
        )}
      </Paper>
    </Box>
  );
}
